import React from 'react';
import FacilityStore from '../stores/FacilityStore';
import ViewerStore from '../stores/ViewerStore';
import AssetTagSearchFilter from './AssetTagSearchFilter';
import AssetActions from '../actions/AssetActions';
import FacilityActions from '../actions/FacilityActions';
import AssetList from './AssetList';
import AssetQueryForm from './AssetQueryForm';
import FacilitySlideoutSessionPanel from './FacilitySlideoutSessionPanel';
import { displayApiError } from '@shared/api/apiClient';
import {
    SessionPanelMeasuredContent,
    SessionPanelToolbar,
    type SessionPanelRenderContext,
} from '../../shared/panels/SessionPanel';
import { FACILITY_SLIDEOUT_PANEL_IDS } from '../helpers/facilitySlideoutPanelSession';
const { useState, useEffect, useRef, useMemo } = React;
const EMPTY_STRING_ARRAY: string[] = [];

interface AssetTagsBarProps {
    mode: "facility" | "panoviewer" | "3dviewer";
}

interface AssetQueryFormHandle {
    open: () => void;
    close: () => void;
}

const TOOLBAR_HEIGHT = 40;
const PANEL_CONTENT_BOTTOM_PADDING = 10;
const ASSET_TAGS_PANEL_ID = FACILITY_SLIDEOUT_PANEL_IDS.assetTags;

const getDefaultPinnedWidth = (mode: "facility" | "panoviewer" | "3dviewer") => {
    return mode === 'facility' ? 400 : 330;
};

const isBarOpen = (mode: "facility" | "panoviewer" | "3dviewer") => {
    return (mode === "facility") ? FacilityStore.getAssetSearchbarOpen() : FacilityStore.getAssetTagbarOpen();
}

const getModeAwareSelectedOntologyId = (mode: "facility" | "panoviewer" | "3dviewer"): string => {
    return (mode === "panoviewer") ? (FacilityStore.getRootOntologyPart()?.ID ?? '') : (FacilityStore.getSelectedOntology()?.ID ?? '');
}

const AssetTagsBar = React.forwardRef((props: AssetTagsBarProps, ref) => {
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(FacilityStore.getSidebarOpen());
    const [isOpen, setIsOpen] = useState<boolean>(() => isBarOpen(props.mode));
    const [facilityAssets, setFacilityAssets] = useState<IFacilityAssetsData>(FacilityStore.getFacilityAssetsData());
    const [selectedID, setSelectedID] = useState<string>(ViewerStore.getSelectedHierarchyItem()?.ID || ViewerStore.getSelectedAnnotation() || ViewerStore.getSelectedAsset());
    const [viewId, setViewId] = useState<string>('');
    const [occludedTags, setOccludedTags] = useState<string[]>(ViewerStore.getOccludedAnnotations() ?? EMPTY_STRING_ARRAY);
    const [filteredAssets, setFilteredAssets] = useState<string[]>(ViewerStore.getFilteredAssets() ?? EMPTY_STRING_ARRAY);
    const [assetFilter, setAssetFilter] = useState<IAssetFilter>(ViewerStore.getAssetTagsFilter());
    const [selectedOntologyId, setSelectedOntologyId] = useState<string>(getModeAwareSelectedOntologyId(props.mode));
    const queryFormRef = useRef<AssetQueryFormHandle>(null);

    const setModeOpen = React.useCallback((open: boolean) => {
        if (props.mode === 'facility') {
            if (open) {
                FacilityActions.openAssetSearchbar();
            } else {
                FacilityActions.closeAssetSearchbar();
            }
            return;
        }

        FacilityActions.toggleAssetTagBar(open, false);
    }, [props.mode]);

    const handlePanelOpenChange = React.useCallback((open: boolean) => {
        setIsOpen(open);
        setModeOpen(open);
    }, [setModeOpen]);

    const mountedRef = React.useRef(true);
    React.useEffect(() => {
        return () => { mountedRef.current = false; };
    }, []);
    React.useEffect(() => {
        const handleFacilityStoreChange = () => { updateFacilityState(); };
        const handleViewerStoreChange = () => { updateViewerState(); };

        FacilityStore.addChangeListener(handleFacilityStoreChange);
        ViewerStore.addChangeListener(handleViewerStoreChange);
        return () => {
            FacilityStore.removeChangeListener(handleFacilityStoreChange);
            ViewerStore.removeChangeListener(handleViewerStoreChange);
        };
    }, []);

    const updateFacilityState = () => {
        setFacilityAssets(prevState => {
           const newState = FacilityStore.getFacilityAssetsData();
           if (!newState || !prevState) {
               return newState;
           }
           return (newState.Facility.ID != prevState.Facility.ID || newState.Assets != prevState.Assets || newState.NativeAssets != prevState.NativeAssets || newState.AssetTags != prevState.AssetTags) ? newState : prevState;
        });

        setSidebarOpen(FacilityStore.getSidebarOpen());
        const nextOpen = isBarOpen(props.mode);
        setIsOpen(prev => (prev === nextOpen ? prev : nextOpen));

        const nextOntologyId = getModeAwareSelectedOntologyId(props.mode);
        setSelectedOntologyId(prev => (prev === nextOntologyId ? prev : nextOntologyId));
    }
    const updateViewerState = () => {
        if (!mountedRef.current) return;

        const nextViewId = ViewerStore.getSelectedViewImage()?.ID ?? '';
        const nextOccluded = ViewerStore.getOccludedAnnotations() ?? [];
        const nextSelected = ViewerStore.getSelectedHierarchyItem()?.ID || ViewerStore.getSelectedAnnotation() || ViewerStore.getSelectedAsset();
        const nextFiltered = ViewerStore.getFilteredAssets() ?? [];

        setViewId(prev => (prev === nextViewId ? prev : nextViewId));
        setOccludedTags(prev => (arraysEqual(prev, nextOccluded) ? prev : nextOccluded));
        setSelectedID(prev => (prev === nextSelected ? prev : nextSelected));
        setFilteredAssets(prev => (arraysEqual(prev, nextFiltered) ? prev : nextFiltered));
        setAssetFilter(ViewerStore.getAssetTagsFilter());
    };

    useEffect(() => {
        const cachedOntologyId = assetFilter?.QueryOntologyID ?? '';
        if (cachedOntologyId === selectedOntologyId) {
            return;
        }

        const queryRules = assetFilter?.QueryRules as { rules?: unknown[] } | null | undefined;
        const hasQueryRules = !!(queryRules && queryRules.rules && queryRules.rules.length > 0);
        if (!selectedOntologyId || !hasQueryRules || !assetFilter?.QueryApiRules) {
            return;
        }

        AssetActions.executeAssetQuery(selectedOntologyId, assetFilter.QueryApiRules, queryRules)
            .catch(displayApiError);
    }, [selectedOntologyId, assetFilter]);

    // Determine if any filter is set (for enabling/disabling reset button)
    const isAnyFilterSet = useMemo(() => {
        if (!assetFilter) return false;
        
        const hasNameFilter = !!(assetFilter.NameFilter && assetFilter.NameFilter.trim() !== '');
        const hasPlacementFilter = assetFilter.PlacementStatusFilter && assetFilter.PlacementStatusFilter !== 'All';
        const hasAssetIdFilter = !!(assetFilter.AssetIdFilter && assetFilter.AssetIdFilter.length > 0);
        const queryRules = assetFilter.QueryRules as { rules?: unknown[] } | null | undefined;
        const hasQueryRules = !!(queryRules && queryRules.rules && queryRules.rules.length > 0);
        
        return hasNameFilter || hasPlacementFilter || hasAssetIdFilter || hasQueryRules;
    }, [assetFilter]);

    // Determine if query filter is active (for highlighting query builder icon)
    const hasQueryFilter = useMemo(() => {
        if (!assetFilter) return false;
        const queryRules = assetFilter.QueryRules as { rules?: unknown[] } | null | undefined;
        return !!(queryRules && queryRules.rules && queryRules.rules.length > 0);
    }, [assetFilter]);

    const arraysEqual = (a?: string[] | null, b?: string[] | null) => {
        if (a === b) return true;
        if (!a || !b) return false;
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
        return true;
    };

    const handleOpenQueryBuilder = () => {
        queryFormRef.current?.open();
    };

    const handleOK = async (apiRules: unknown, cleanedRules: unknown) => {
        await AssetActions.executeAssetQuery(selectedOntologyId, apiRules, cleanedRules);
    };

    const handleResetAllFilters = () => {
        // Get all asset class IDs for the default filter
        const assetClasses = FacilityStore.getAssetClasses() || [];
        const allClassIds = ['select_all', ...assetClasses.map((ac: any) => ac.ID)];
        
        // Build default filter with all classes selected
        const defaultFilter: IAssetFilter = {
            NameFilter: '',
            AssetClassFilter: allClassIds,
            PlacementStatusFilter: 'All',
            AssetIdFilter: undefined,
            QueryRules: undefined,
            QueryApiRules: undefined,
            QueryOntologyID: undefined,
        };
        
        AssetActions.updateAssetSearchFilter(defaultFilter);
    };

    const filterDivRef = useRef<HTMLDivElement>(null);
    const [filterHeight, setFilterHeight] = useState(0);
    const contentDivName = `asset-tags-bar-content-${props.mode}`;

    useEffect(() => {
        const resizeObserver = new ResizeObserver((entries) => {
            setFilterHeight(entries[0]?.contentRect.height);
        });

        if (filterDivRef.current) {
            resizeObserver.observe(filterDivRef.current);
        }

        return () => {
            if (filterDivRef.current) {
                resizeObserver.unobserve(filterDivRef.current);
            }
        };
    }, [isOpen]);

    let tagbarClass = ''
    if (isOpen) {
        tagbarClass = sidebarOpen ? 'show-extend' : 'show';
        tagbarClass += (props.mode === "facility") ? ' static-position' : ' fixed-position';
    }

    const renderPanelBody = (panelContext: SessionPanelRenderContext) => (<>
        {/* Query Builder Toolbar */}
        <SessionPanelToolbar
            attachmentButtonId={`panel-attachment-icon-${props.mode}`}
            closeButtonId={`panel-close-icon-${props.mode}`}
            closeClassName="panel-close-icon"
            context={panelContext}
            itemClassName="asset-tag-tool-bar-item enabled-btn"
            listClassName="asset-tag-tool-bar-list"
            spacerClassName="asset-tag-tool-bar-spacer"
            toolbarClassName="asset-tag-tool-bar"
            toggleClassName="panel-attachment-icon"
        >
            <li
                className={`asset-tag-tool-bar-item enabled-btn ${hasQueryFilter ? 'active' : ''}`}
                id="query-builder-icon"
                title={InovxLoader.t('facility:form.queryBuilder')}
                onClick={handleOpenQueryBuilder}
            ></li>
            <li
                className={`asset-tag-tool-bar-item ${isAnyFilterSet ? 'enabled-btn' : ''}`}
                id="reset-filters-icon"
                title={InovxLoader.t('facility:assetTagFilter.resetAllFilters')}
                onClick={isAnyFilterSet ? handleResetAllFilters : undefined}
            ></li>
        </SessionPanelToolbar>

        <div ref={filterDivRef}>
            <AssetTagSearchFilter includeViewPlacementFilter={(props.mode === 'panoviewer')} />
        </div>
        <SessionPanelMeasuredContent
            bottomPadding={PANEL_CONTENT_BOTTOM_PADDING}
            fallbackOffset={filterHeight + TOOLBAR_HEIGHT}
            minHeight={120}
        >
            {gridHeight => (
                <AssetList
                    mode={props.mode}
                    facilityAssets={facilityAssets}
                    selectedOntologyId={selectedOntologyId}
                    selectedID={selectedID}
                    viewId={viewId}
                    occludedTags={occludedTags}
                    filteredAssets={filteredAssets}
                    assetFilter={assetFilter}
                    gridHeight={gridHeight}
                    onSelectedIDChange={setSelectedID}
                />
            )}
        </SessionPanelMeasuredContent>

        <AssetQueryForm
            ref={queryFormRef}
            assetClasses={FacilityStore.getSelectedFacility()?.AssetClasses}
            initialRules={assetFilter?.QueryRules}
            onOK={handleOK}
        />
    </>);

    return (
        <FacilitySlideoutSessionPanel
            attachedClassName={tagbarClass}
            cancelSelector=".asset-tag-tool-bar-item, input, button, .e-control, .e-popup, .e-dlg-container"
            className="asset-tag-bar"
            elementId={contentDivName}
            floatingClassName="show floating-position"
            isOpen={isOpen}
            onOpenChange={handlePanelOpenChange}
            panelId={ASSET_TAGS_PANEL_ID}
            pinnedWidth={getDefaultPinnedWidth(props.mode)}
        >
            {renderPanelBody}
        </FacilitySlideoutSessionPanel>
    );
});

export default AssetTagsBar;
